import { useEffect, useRef, useState } from 'react';
import maintenanceService, { type MaintenanceWindowView } from '../services/maintenance.service';

const POLL_INTERVAL_MS = 60_000;

export interface UseActiveMaintenanceResult {
  window: MaintenanceWindowView | null;
  loading: boolean;
  /** ms remaining until `starts_at`. Negative once the window started. */
  msUntilStart: number | null;
  /** True once `now >= starts_at` (downtime is in effect). */
  startedNow: boolean;
}

/**
 * Polls `/maintenance/active` every minute and exposes the soonest
 * scheduled-and-visible window to the UI. The countdown updates every
 * second locally — the poll only refreshes the underlying record.
 *
 * Returning a single object (rather than separate calls) keeps banner +
 * force-logout consumers in lock-step with the same `now` evaluation.
 */
export function useActiveMaintenance(
  enabled: boolean = true,
  usePublic: boolean = false,
): UseActiveMaintenanceResult {
  const [window, setWindow] = useState<MaintenanceWindowView | null>(null);
  const [loading, setLoading] = useState<boolean>(enabled);
  // Current time, refreshed on a 1Hz interval so the countdown re-renders
  // without re-polling the API. Read directly in the msUntilStart math below.
  const [now, setNow] = useState<number>(() => Date.now());
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    if (!enabled) {
      setWindow(null);
      setLoading(false);
      return;
    }

    let pollHandle: number | undefined;
    const fetchOnce = async (): Promise<void> => {
      try {
        const res = usePublic
          ? await maintenanceService.getPublicStatus()
          : await maintenanceService.getActive();
        if (!cancelledRef.current) setWindow(res.window);
      } catch {
        // Network blip — keep last-known state. Next tick will retry.
      } finally {
        if (!cancelledRef.current) setLoading(false);
      }
    };

    fetchOnce();
    pollHandle = setInterval(fetchOnce, POLL_INTERVAL_MS) as unknown as number;

    return () => {
      cancelledRef.current = true;
      if (pollHandle !== undefined) clearInterval(pollHandle);
    };
  }, [enabled, usePublic]);

  // Local 1Hz tick to re-render the countdown without re-polling the API.
  useEffect(() => {
    if (!enabled || !window) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [enabled, window]);

  let msUntilStart: number | null = null;
  let startedNow = false;
  if (window) {
    const startsAt = new Date(window.starts_at).getTime();
    msUntilStart = startsAt - now;
    startedNow = msUntilStart <= 0;
  }

  return { window, loading, msUntilStart, startedNow };
}
